import {
  InvitationStatus,
  InvitableInternalRole,
  OrganizationStatus,
  ParticipantRole,
} from "@/enum/common.enum";

export const INVITATION_STATUS_STYLES = {
  [InvitationStatus.PENDING]: "neutral",
  [InvitationStatus.ACCEPTED]: "success",
  [InvitationStatus.EXPIRED]: "danger",
  [InvitationStatus.REVOKED]: "neutral",
} as const;

export const ORGANIZATION_ROLE_OPTIONS = [
  { value: InvitableInternalRole.BROKERAGE_ADMIN, label: "Brokerage admin" },
  { value: InvitableInternalRole.TEAM_LEAD, label: "Team lead" },
  { value: InvitableInternalRole.AGENT, label: "Agent" },
  { value: InvitableInternalRole.COORDINATOR, label: "Coordinator" },
];

export const PARTICIPANT_ROLE_OPTIONS = [
  { value: ParticipantRole.CLIENT, label: "Client" },
  { value: ParticipantRole.CO_AGENT, label: "Co-agent (opposing side)" },
  { value: ParticipantRole.LENDER, label: "Lender" },
  { value: ParticipantRole.ATTORNEY, label: "Attorney" },
  { value: ParticipantRole.VENDOR, label: "Vendor" },
];

export const BILLING_BADGES: Record<OrganizationStatus, { label: string; bg: string; warn?: boolean }> = {
  [OrganizationStatus.TRIALING]: { label: "Trial", bg: "bg-bone text-stone border border-edge" },
  [OrganizationStatus.ACTIVE]: { label: "Active", bg: "bg-moss text-linen" },
  [OrganizationStatus.PAST_DUE]: { label: "Billing", bg: "bg-rust text-linen", warn: true },
  [OrganizationStatus.CANCELED]: { label: "Canceled", bg: "bg-stone text-linen", warn: true },
  [OrganizationStatus.GRACE]: { label: "Grace", bg: "bg-rust text-linen", warn: true },
};

export const BILLING_STATUS_STYLES: Record<string, { label: string; tone: string }> = {
  trialing: { label: "Trial", tone: "neutral" },
  active: { label: "Active", tone: "success" },
  past_due: { label: "Payment needed", tone: "danger" },
  canceled: { label: "Canceled", tone: "neutral" },
  grace: { label: "Grace period", tone: "warning" },
};

// In-app "Upgrade to …" card. Signed-in owners may see that Team and Growth lift
// the active-deal cap (the public site never quotes deal counts); Individual's
// own cap shows in the usage meter rather than as a selling point.
export const BILLING_PLAN_FEATURES: Record<string, string[]> = {
  solo_monthly: ["1 agent seat + 1 assistant seat", "Free client, lender & attorney collaborators", "Buyer & seller pathways · calm client view"],
  team_monthly: ["5 agent seats + 1 admin seat", "One shared team workspace", "No active-deal cap", "Team roles & audit"],
  growth_monthly: ["10 agent seats + 1 admin seat", "One shared team workspace", "No active-deal cap", "Everything in Team", "Priority support and guided setup"],
  brokerage: ["Multi-team seat pooling", "Priced by office size + rollout scope", "White-glove onboarding", "Consolidated billing + reporting"],
  enterprise: ["Custom seat + deal envelopes", "Dedicated success partner", "SSO / SAML on request", "Bespoke rollout"],
};

export const AUDIT_ACTION_GROUPS = [
  { key: "", label: "Everything" },
  { key: "deal.", label: "Deals" },
  { key: "task.", label: "Tasks" },
  { key: "document.", label: "Documents" },
  { key: "invitation.", label: "Invitations" },
  { key: "billing.", label: "Billing" },
  { key: "participant.", label: "Participants" },
  { key: "permission.", label: "Permissions" },
  { key: "security.", label: "Security" },
];

// Per-category tones, matching the audit-log design. Each is a translucent
// wash of its category colour with the solid colour as text.
export const AUDIT_ACTION_BADGE_STYLES = {
  deal: "bg-dash-greenDeep/10 text-dash-greenDeep",
  task: "bg-dash-muted/10 text-dash-muted",
  document: "bg-dash-slate/10 text-dash-slate",
  invitation: "bg-dash-slate/10 text-dash-slate",
  billing: "bg-dash-slate/10 text-dash-slate",
  participant: "bg-dash-greenMid/10 text-dash-greenMid",
  permission: "bg-dash-amber/10 text-dash-amber",
  security: "bg-dash-rust/10 text-dash-rust",
  default: "bg-dash-chip text-dash-muted",
} as const;

export const INVITATION_VIEW_TABS = [
  { key: "org", label: "Internal Team" },
  { key: "deal", label: "External Team" },
];

export const REPORT_RANGE_OPTIONS = [
  { value: "7", label: "Last 7 days" }, { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" }, { value: "all", label: "All time" },
];

